/**
 * Picture-naming trial-builder helpers (fix #252 / #PL-WEB-RUBRICS).
 *
 * Pure functions only — no React, no DOM. Lives in a `.ts` file
 * (sibling of the renderer .tsx) so the lint `only-export-components`
 * rule doesn't fire on the renderer file.
 */

export interface PictureNamingTrial {
  image_url: string;
  /** Expected word (case-insensitive match against the kid's tap). */
  expected: string;
  choices: string[];
}

export function buildPictureNamingTrials(
  payload: unknown,
  count: number,
): PictureNamingTrial[] {
  const trials: PictureNamingTrial[] = [];
  if (
    payload &&
    typeof payload === 'object' &&
    Array.isArray((payload as { trials?: unknown }).trials)
  ) {
    const rawTrials = (payload as { trials: unknown[] }).trials;
    for (let i = 0; i < Math.min(count, rawTrials.length); i += 1) {
      const t = rawTrials[i];
      if (
        t &&
        typeof t === 'object' &&
        typeof (t as { image_url?: unknown }).image_url === 'string' &&
        typeof (t as { expected?: unknown }).expected === 'string' &&
        Array.isArray((t as { choices?: unknown }).choices)
      ) {
        const choices = ((t as { choices: unknown[] }).choices).filter(
          (c): c is string => typeof c === 'string',
        );
        if (choices.length > 0) {
          trials.push({
            image_url: (t as { image_url: string }).image_url,
            expected: (t as { expected: string }).expected,
            choices,
          });
        }
      }
    }
  }
  // Do NOT pad with fabricated "cat/dog/pig/cow" trials when the
  // server payload has fewer valid trials than `count` (fix #292).
  // Padding pollutes per-item accuracy analytics — synthetic trials
  // were being scored and POSTed to api-record-practice-attempts
  // against a real practice_set_item_id. Callers should clamp the
  // playable count to the length of the returned array and treat
  // zero valid trials as unplayable.
  return trials;
}
